import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import ResourceList from "../../../../components/resourceList/ResourceList";
import Button from "../../../../components/button/Button";
import styles from "./FiscalDeviceBillingScreen.module.scss";
import classNames from "classnames";
import moment from "moment";
import {
  fetchBillingInvoices,
} from "../../../../store/actions/admin/billingInvoices";
import taxPayersService from "../../../../apis/services/admin/taxPayersService";

const INVOICES_HEADER = [
  { label: "BROJ FAKTURE", key: "invoiceNumber", sortable: false },
  { label: "PERIOD", sortable: false },
  { label: "IZDAT", key: "issueDate", sortable: false },
  { label: "ROK", key: "dueDate", sortable: false },
  { label: "STATUS", key: "status", sortable: false },
  { label: "PLAĆENO", key: "isPaid", sortable: false },
  { label: "IZNOS", key: "total", sortable: false },
  { label: "AKCIJE", sortable: false },
];

const SCHEMAS_HEADER = [
  { label: "NAZIV", key: "name", sortable: false },
  { label: "FAKTURISKA KOMPANIJA", key: "billingCompany", sortable: false },
  { label: "TIP PERIODA", key: "billingPeriodType", sortable: false },
  { label: "AKTIVNA", key: "isActive", sortable: false },
  { label: "AKCIJE", sortable: false },
];

const FiscalDeviceBillingScreen = ({ id, companyId }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("schemas");
  const [schemas, setSchemas] = useState([]);
  const [loading, setLoading] = useState(true);
  const isMountedRef = useRef(true);

  const invoices = useSelector((state) => state.billingInvoices.invoices);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (companyId) {
      loadCompanyInfo();
      loadInvoices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    if (companyId && activeTab === "invoices") {
      loadInvoices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, companyId]);

  const loadCompanyInfo = async () => {
    try {
      if (!isMountedRef.current || !companyId) return;
      setLoading(true);
      const response = await taxPayersService.fetchCompanyInfo(companyId);
      if (!isMountedRef.current) return;
      
      const invoiceSchemasData = response.data?.invoiceSchemas || [];
      setSchemas(invoiceSchemasData);
    } catch (error) {
      if (!isMountedRef.current) return;
      console.error("Error loading company info:", error);
      toast.error("Greška pri učitavanju podataka");
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const loadInvoices = () => {
    if (companyId) {
      dispatch(fetchBillingInvoices(1, 20, companyId, null, "", null, null, null));
    }
  };

  const renderInvoiceRow = (invoice, index) => {
    const billingPeriodStart = invoice.billingPeriodStart
      ? moment(invoice.billingPeriodStart).format("DD.MM.YYYY")
      : "-";
    const billingPeriodEnd = invoice.billingPeriodEnd
      ? moment(invoice.billingPeriodEnd).format("DD.MM.YYYY")
      : "-";
    const period = `${billingPeriodStart} - ${billingPeriodEnd}`;
    const issueDate = invoice.issueDate
      ? moment(invoice.issueDate).format("DD.MM.YYYY")
      : "-";
    const dueDate = invoice.dueDate
      ? moment(invoice.dueDate).format("DD.MM.YYYY")
      : "-";

    return (
      <div
        className={classNames(styles.row, index % 2 === 0 ? styles.even : styles.odd)}
        key={invoice.id}
      >
        <div className={styles.cell}>
          <p className={styles.invoiceNumber}>{invoice.invoiceNumber}</p>
        </div>
        <div className={styles.cell}>
          <p className={styles.period}>{period}</p>
        </div>
        <div className={styles.cell}>
          <p>{issueDate}</p>
        </div>
        <div className={styles.cell}>
          <p>{dueDate}</p>
        </div>
        <div className={styles.cell}>
          <span className={classNames(styles.statusBadge, styles[invoice.status])}>
            {invoice.status === "draft" ? "Draft" : invoice.status === "sent" ? "Poslato" : invoice.status === "paid" ? "Plaćeno" : invoice.status === "overdue" ? "Zakasnelo" : "Otkazano"}
          </span>
        </div>
        <div className={styles.cell}>
          <span className={classNames(styles.statusBadge, invoice.isPaid ? styles.paid : styles.unpaid)}>
            {invoice.isPaid ? "Da" : "Ne"}
          </span>
        </div>
        <div className={styles.cell}>
          <p className={styles.amount}>
            {typeof invoice.total === "number" ? invoice.total.toFixed(2) : (parseFloat(invoice.total) || 0).toFixed(2)} RSD
          </p>
        </div>
        <div className={styles.cell}>
          <button
            className={styles.viewButton}
            onClick={() => history.push(`/billing-invoices/${invoice.id}`)}
          >
            Pregled
          </button>
        </div>
      </div>
    );
  };

  const renderSchemaRow = (schema, index) => {
    return (
      <div
        className={classNames(styles.row, index % 2 === 0 ? styles.even : styles.odd)}
        key={schema.id}
      >
        <div className={styles.cell}>
          <p>{schema.name || "-"}</p>
        </div>
        <div className={styles.cell}>
          <p>{schema.billingCompany?.name || "-"}</p>
        </div>
        <div className={styles.cell}>
          <p>
            {schema.defaultBillingPeriodType === "monthly" ? "Mesečno" :
             schema.defaultBillingPeriodType === "quarterly" ? "Kvartalno" :
             schema.defaultBillingPeriodType === "semi-annual" ? "Polugodišnje" :
             schema.defaultBillingPeriodType === "annual" ? "Godišnje" : "-"}
          </p>
        </div>
        <div className={styles.cell}>
          <span className={classNames(styles.statusBadge, schema.isActive ? styles.active : styles.inactive)}>
            {schema.isActive ? "Da" : "Ne"}
          </span>
        </div>
        <div className={styles.cell}>
          <button
            className={styles.viewButton}
            onClick={() => history.push(`/invoice-schemas/${schema.id}`)}
            style={{ marginRight: "8px" }}
          >
            Pregled
          </button>
          <button
            className={styles.editButton}
            onClick={() => history.push(`/invoice-schemas/${schema.id}/edit`)}
          >
            Izmeni
          </button>
        </div>
      </div>
    );
  };

  if (!companyId) {
    return <div className={styles.loading}>Učitavanje podataka o kompaniji...</div>;
  }

  if (loading) {
    return <div className={styles.loading}>Učitavanje...</div>;
  }

  return (
    <div className={styles.content}>
      <div className={styles.tabs}>
        <button
          className={classNames(styles.tab, activeTab === "schemas" && styles.active)}
          onClick={() => setActiveTab("schemas")}
        >
          Šeme faktura ({schemas.length})
        </button>
        <button
          className={classNames(styles.tab, activeTab === "invoices" && styles.active)}
          onClick={() => setActiveTab("invoices")}
        >
          Fakture ({invoices?.data?.total || 0})
        </button>
      </div>

      <div className={styles.tabContent}>
        {activeTab === "schemas" && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Šeme faktura</h2>
              {companyId && (
                <Button
                  name="+ Nova šema"
                  onClick={() => history.push(`/invoice-schemas/new?companyId=${companyId}`)}
                />
              )}
            </div>
            <ResourceList
              resource={{ 
                data: Array.isArray(schemas) ? schemas : [], 
                total: schemas.length, 
                page: 1, 
                perPage: 100 
              }}
              onSort={() => {}}
              onLoadPage={() => {}}
              headerValues={SCHEMAS_HEADER}
              rowRender={renderSchemaRow}
              showLoader={false}
            />
          </div>
        )}

        {activeTab === "invoices" && (
          <div className={styles.section}>
            <ResourceList
              resource={invoices.data || { data: [], total: 0, page: 1, perPage: 20 }}
              onSort={() => {}}
              onLoadPage={(page) => {
                dispatch(fetchBillingInvoices(page, invoices.data?.perPage || 20, companyId, null, "", null, null, null));
              }}
              headerValues={INVOICES_HEADER}
              rowRender={renderInvoiceRow}
              showLoader={true}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default FiscalDeviceBillingScreen;

